'use client';
import React, { useState, useEffect } from 'react';

import { useParams } from 'next/navigation';
import Topbar from '@/components/Topbar';
import Footer from '@/components/Footer';
import { CheckCircle, XCircle, Search, Shield, Award, Calendar, User, BookOpen, Globe } from 'lucide-react';

interface CertificateData {
  id: string;
  studentName: string;
  olympiad: string;
  grade: string;
  score: string;
  percentile: string;
  globalRank: string;
  meritLevel: string;
  issueDate: string;
  schoolName: string;
  country: string;
  valid: boolean;
}

const MOCK_CERTIFICATES: Record<string, CertificateData> = {
  'FM-2026-MATH-001234': {
    id: 'FM-2026-MATH-001234',
    studentName: 'A**** M.',
    olympiad: 'International Mathematics Olympiad',
    grade: 'Grade 10',
    score: '148 / 150',
    percentile: '99.9th',
    globalRank: '#1',
    meritLevel: 'Gold — Distinction',
    issueDate: 'April 15, 2026',
    schoolName: 'Delhi Public School',
    country: 'India',
    valid: true,
  },
  'FM-2026-SCI-005678': {
    id: 'FM-2026-SCI-005678',
    studentName: 'Lena M.',
    olympiad: 'International Science Olympiad',
    grade: 'Grade 10',
    score: '145 / 150',
    percentile: '99.7th',
    globalRank: '#3',
    meritLevel: 'Gold — Distinction',
    issueDate: 'April 15, 2026',
    schoolName: 'Gymnasium Heidelberg',
    country: 'Germany',
    valid: true,
  },
};

type VerifyStatus = 'idle' | 'loading' | 'valid' | 'invalid';

export default function CertificateVerifyPage() {
  const params = useParams();
  const certIdParam = params?.certId as string | undefined;

  const [inputId, setInputId] = useState('');
  const [status, setStatus] = useState<VerifyStatus>('idle');
  const [certData, setCertData] = useState<CertificateData | null>(null);

  useEffect(() => {
    if (certIdParam && certIdParam !== 'verify') {
      setInputId(certIdParam);
      verifyCertificate(certIdParam);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certIdParam]);

  function verifyCertificate(id: string) {
    const trimmed = id.trim().toUpperCase();
    if (!trimmed) return;
    setStatus('loading');
    setTimeout(() => {
      const found = MOCK_CERTIFICATES[trimmed];
      if (found) {
        setCertData(found);
        setStatus('valid');
      } else {
        setCertData(null);
        setStatus('invalid');
      }
    }, 900);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    verifyCertificate(inputId);
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Topbar />
      <main className="flex-1 pt-16">
        {/* Hero */}
        <section className="bg-hero-gradient py-16 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/80 text-xs font-semibold uppercase tracking-widest mb-6">
              <Shield size={12} />
              Certificate Verification
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-4 leading-tight">
              Verify Certificate Authenticity
            </h1>
            <p className="text-slate-300 text-base md:text-lg max-w-xl mx-auto">
              Enter a FutureMinds.ai certificate ID to instantly confirm its validity, student details, and achievement level.
            </p>
          </div>
        </section>

        {/* Verify Form */}
        <section className="py-12 px-6">
          <div className="max-w-2xl mx-auto">
            <div className="bg-card border border-border rounded-2xl shadow-sm p-8">
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={inputId}
                  onChange={(e) => setInputId(e.target.value)}
                  placeholder="e.g. FM-2026-MATH-001234"
                  className="flex-1 px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground"
                />
                <button
                  type="submit"
                  disabled={status === 'loading' || !inputId.trim()}
                  className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Search size={16} />
                  {status === 'loading' ? 'Verifying…' : 'Verify'}
                </button>
              </form>
              <p className="text-xs text-muted-foreground mt-3">
                Certificate IDs follow the format: <span className="font-mono text-foreground">FM-YYYY-SUBJECT-XXXXXX</span>
              </p>
            </div>

            {/* Loading */}
            {status === 'loading' && (
              <div className="mt-8 text-center py-12">
                <div className="inline-block w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin mb-4" />
                <p className="text-muted-foreground text-sm">Checking certificate database…</p>
              </div>
            )}

            {/* Valid */}
            {status === 'valid' && certData && (
              <div className="mt-8 fade-in">
                <div className="flex items-center gap-3 mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <CheckCircle size={24} className="text-green-600 shrink-0" />
                  <div>
                    <p className="font-bold text-green-800 text-sm">Certificate Verified ✓</p>
                    <p className="text-green-700 text-xs mt-0.5">This certificate is authentic and was issued by FutureMinds.ai</p>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
                  {/* Certificate header */}
                  <div className="certificate-bg p-6 text-center border-b border-amber-200">
                    <Award size={32} className="text-amber-600 mx-auto mb-2" />
                    <p className="text-xs font-semibold uppercase tracking-widest text-amber-700 mb-1">Certificate of Achievement</p>
                    <h2 className="text-2xl font-extrabold text-amber-900">{certData.studentName}</h2>
                    <p className="text-amber-700 text-sm mt-1">{certData.olympiad}</p>
                    <span className="inline-block mt-3 px-4 py-1 bg-amber-400 text-amber-900 text-xs font-bold rounded-full">
                      {certData.meritLevel}
                    </span>
                  </div>

                  {/* Details grid */}
                  <div className="p-6 grid grid-cols-2 md:grid-cols-3 gap-4">
                    {[
                      { icon: <User size={14} />, label: 'Student', value: certData.studentName },
                      { icon: <BookOpen size={14} />, label: 'Olympiad', value: certData.olympiad },
                      { icon: <Award size={14} />, label: 'Grade', value: certData.grade },
                      { icon: <Award size={14} />, label: 'Score', value: certData.score },
                      { icon: <Award size={14} />, label: 'Percentile', value: certData.percentile },
                      { icon: <Globe size={14} />, label: 'Global Rank', value: certData.globalRank },
                      { icon: <User size={14} />, label: 'School', value: certData.schoolName },
                      { icon: <Globe size={14} />, label: 'Country', value: certData.country },
                      { icon: <Calendar size={14} />, label: 'Issued', value: certData.issueDate },
                    ].map((item) => (
                      <div key={item.label} className="bg-muted rounded-xl p-3">
                        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                          {item.icon}
                          <span className="text-xs font-semibold uppercase tracking-wide">{item.label}</span>
                        </div>
                        <p className="text-sm font-semibold text-foreground">{item.value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="px-6 pb-6">
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-xl">
                      <Shield size={14} className="text-primary shrink-0" />
                      <span className="text-xs text-muted-foreground font-mono">Certificate ID: {certData.id}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Invalid */}
            {status === 'invalid' && (
              <div className="mt-8 fade-in">
                <div className="flex items-center gap-3 p-5 bg-red-50 border border-red-200 rounded-xl">
                  <XCircle size={24} className="text-red-500 shrink-0" />
                  <div>
                    <p className="font-bold text-red-800 text-sm">Certificate Not Found</p>
                    <p className="text-red-700 text-xs mt-0.5">
                      No certificate matching <span className="font-mono font-semibold">{inputId.trim().toUpperCase()}</span> was found. Please check the ID and try again.
                    </p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4 text-center">
                  If you believe this is an error, contact{' '}
                  <a href="mailto:support@futureminds.ai" className="text-primary hover:underline">support@futureminds.ai</a>
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Trust signals */}
        <section className="py-12 px-6 bg-muted/40 border-t border-border">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center text-lg font-bold text-foreground mb-8">Why Certificate Verification Matters</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: <Shield size={20} className="text-primary" />, title: 'Tamper-Proof', desc: 'Every certificate is cryptographically signed and stored immutably in our database.' },
                { icon: <Globe size={20} className="text-secondary" />, title: 'Globally Recognised', desc: 'Accepted by universities and institutions across 40+ countries as proof of academic excellence.' },
                { icon: <CheckCircle size={20} className="text-green-600" />, title: 'Instant Verification', desc: 'Schools, parents, and admissions officers can verify any certificate in under 2 seconds.' },
              ].map((item) => (
                <div key={item.title} className="bg-card border border-border rounded-xl p-5">
                  <div className="mb-3">{item.icon}</div>
                  <h3 className="font-bold text-foreground text-sm mb-1">{item.title}</h3>
                  <p className="text-muted-foreground text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
